import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { getUserById, deleteUser } from "@/services/userService";
import { UserRole, UserLevel } from "@/types/user";
import {
  ArrowLeft,
  Pencil,
  Trash2,
  User,
  Mail,
  Phone,
  Shield,
  Award,
  Calendar,
  UserCircle,
} from "lucide-react";
import { toast } from "sonner";
import { queryKeys } from "@/lib/queryKeys";

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

function toDate(val: unknown): Date | null {
  if (!val) return null;
  if (val instanceof Date) return val;
  if (typeof (val as { toDate?: () => Date }).toDate === "function")
    return (val as { toDate(): Date }).toDate();
  if (typeof val === "object" && val !== null && "seconds" in val)
    return new Date((val as { seconds: number }).seconds * 1000);
  return null;
}

function formatDate(val: unknown): string {
  const d = toDate(val);
  if (!d) return "--";
  return d.toLocaleDateString("es-DO", { day: "2-digit", month: "long", year: "numeric" });
}

const roleLabels: Record<string, string> = {
  [UserRole.ADMIN]: "Administrador",
  [UserRole.CLIENT]: "Cliente",
  [UserRole.SELLER]: "Vendedor",
  [UserRole.MECHANIC]: "Mecánico",
  [UserRole.GUARANTEER]: "Garantizador",
  [UserRole.SUPPLIER]: "Suplidor",
};

const roleBadgeClass: Record<string, string> = {
  [UserRole.ADMIN]: "bg-red-100 text-red-700 border-red-300",
  [UserRole.SELLER]: "bg-blue-100 text-blue-700 border-blue-300",
  [UserRole.MECHANIC]: "bg-orange-100 text-orange-700 border-orange-300",
  [UserRole.GUARANTEER]: "bg-purple-100 text-purple-700 border-purple-300",
  [UserRole.SUPPLIER]: "bg-teal-100 text-teal-700 border-teal-300",
  [UserRole.CLIENT]: "bg-gray-100 text-gray-700 border-gray-300",
};

const levelBadgeClass: Record<string, string> = {
  [UserLevel.BRONZE]: "bg-orange-100 text-orange-700 border-orange-300",
  [UserLevel.SILVER]: "bg-gray-100 text-gray-600 border-gray-300",
  [UserLevel.GOLD]: "bg-yellow-100 text-yellow-700 border-yellow-300",
  [UserLevel.PLATINUM]: "bg-blue-100 text-blue-700 border-blue-300",
  [UserLevel.DIAMOND]: "bg-purple-100 text-purple-700 border-purple-300",
};

function Spec({ label, value, icon }: { label: string; value?: string | null; icon?: React.ReactNode }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2">
      {icon && <span className="mt-0.5 text-muted-foreground">{icon}</span>}
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-medium">{value}</p>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                    */
/* ------------------------------------------------------------------ */

export function UserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: user, isLoading } = useQuery({
    queryKey: queryKeys.user(id!),
    queryFn: () => getUserById(id!),
    enabled: Boolean(id),
  });

  const handleDelete = async () => {
    if (!id) return;
    if (!confirm("¿Eliminar este usuario? Esta acción no se puede deshacer.")) return;
    try {
      await deleteUser(id);
      toast.success("Usuario eliminado");
      await queryClient.invalidateQueries({ queryKey: queryKeys.users });
      navigate("/users");
    } catch {
      toast.error("Error al eliminar el usuario");
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/users"><ArrowLeft className="h-4 w-4" /></Link>
          </Button>
          <h1 className="font-display text-2xl font-semibold">Usuario</h1>
        </div>
        <p className="text-muted-foreground">Cargando...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/users"><ArrowLeft className="h-4 w-4" /></Link>
          </Button>
          <h1 className="font-display text-2xl font-semibold">Usuario</h1>
        </div>
        <Card>
          <CardContent className="py-12 text-center">
            <UserCircle className="mx-auto h-12 w-12 text-muted-foreground/50" />
            <p className="mt-4 text-muted-foreground">Usuario no encontrado</p>
            <Button asChild className="mt-4">
              <Link to="/users">Volver a usuarios</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const displayName =
    user.displayName ?? ([user.firstName, user.lastName].filter(Boolean).join(" ") || "Sin nombre");

  return (
    <div className="max-w-3xl space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/users">
              <ArrowLeft className="h-4 w-4" />
              <span className="sr-only">Volver</span>
            </Link>
          </Button>
          <div className="flex items-center gap-4">
            {user.photoURL ? (
              <img src={user.photoURL} alt="" className="h-14 w-14 rounded-full object-cover border border-border" />
            ) : (
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-xl font-semibold text-primary">
                {displayName.charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <h1 className="font-display text-2xl font-semibold">{displayName}</h1>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="outline" className={roleBadgeClass[user.role as string] ?? ""}>
                  {roleLabels[user.role as string] ?? user.role}
                </Badge>
                {user.level && (
                  <Badge variant="outline" className={levelBadgeClass[user.level] ?? ""}>
                    {user.level}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="gap-2" asChild>
            <Link to={`/users/edit/${user.id}`}>
              <Pencil className="h-4 w-4" />
              Editar
            </Link>
          </Button>
          <Button variant="outline" className="gap-2 text-destructive" onClick={handleDelete}>
            <Trash2 className="h-4 w-4" />
            Eliminar
          </Button>
        </div>
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        {/* Contact info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <User className="h-4 w-4" />
              Información de contacto
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-0">
            <Spec label="Email" value={user.email} icon={<Mail className="h-4 w-4" />} />
            <Spec label="Teléfono" value={user.phone} icon={<Phone className="h-4 w-4" />} />
            <Spec
              label="Nombre completo"
              value={[user.firstName, user.lastName].filter(Boolean).join(" ") || null}
              icon={<UserCircle className="h-4 w-4" />}
            />
          </CardContent>
        </Card>

        {/* Role & Level */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Shield className="h-4 w-4" />
              Rol y nivel
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-0">
            <Spec
              label="Rol"
              value={roleLabels[user.role as string] ?? (user.role as string)}
              icon={<Shield className="h-4 w-4" />}
            />
            <Spec
              label="Nivel"
              value={user.level}
              icon={<Award className="h-4 w-4" />}
            />
          </CardContent>
        </Card>

        {/* Metadata */}
        <Card className="sm:col-span-2">
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" />
                Creado: {formatDate(user.createdAt)}
              </div>
              <div className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" />
                Actualizado: {formatDate(user.updatedAt)}
              </div>
              <div className="font-mono text-xs">
                UID: {user.uid ?? user.id}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
